import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag, Trash2, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

function formatPrice(price) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
}

function AddonLine({ addon }) {
  var text = "+ " + addon.quantity + "x " + addon.name + " (" + formatPrice(addon.price * addon.quantity) + ")";
  return <p className="text-xs text-[#A1A1AA]">{text}</p>;
}

function CartItemAddons({ addons }) {
  if (!addons || addons.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {addons.map(function(addon) {
        return <AddonLine key={addon.id} addon={addon} />;
      })}
    </div>
  );
}

function CartItemImage({ imgSrc, name }) {
  if (imgSrc) {
    return <img src={imgSrc} alt={name} className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full flex items-center justify-center">
      <ShoppingBag className="w-8 h-8 text-[#3f3f46]" />
    </div>
  );
}

function CartItem({ item, onUpdateQty, onRemove }) {
  var imgSrc = item.images && item.images.length > 0 ? item.images[0] : null;
  var basePrice = item.sale_price || item.price;
  var addonTotal = 0;
  if (item.selectedAddons && item.selectedAddons.length > 0) {
    for (var i = 0; i < item.selectedAddons.length; i++) {
      addonTotal += item.selectedAddons[i].price * item.selectedAddons[i].quantity;
    }
  }
  var itemPrice = basePrice + addonTotal;
  var cartItemId = item.cartItemId || item.id;
  
  function handleDecrease() { onUpdateQty(cartItemId, item.quantity - 1); }
  function handleIncrease() { onUpdateQty(cartItemId, item.quantity + 1); }
  function handleRemove() { onRemove(cartItemId); }

  return (
    <div className="flex gap-4 p-4 bg-[#121212] border border-white/5 rounded-sm" data-testid={"cart-item-" + item.id}>
      <div className="w-20 h-20 bg-[#1c1c1c] rounded-sm overflow-hidden flex-shrink-0">
        <CartItemImage imgSrc={imgSrc} name={item.name} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading font-bold text-white text-sm uppercase line-clamp-2">{item.name}</h3>
        <CartItemAddons addons={item.selectedAddons} />
        <p className="text-[#C0A062] font-heading font-bold mt-1">{formatPrice(itemPrice)}</p>
        <div className="flex items-center gap-2 mt-2">
          <button onClick={handleDecrease} className="w-7 h-7 flex items-center justify-center bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-sm">
            <Minus className="w-3 h-3 text-white" />
          </button>
          <span className="w-8 text-center text-white font-mono text-sm">{item.quantity}</span>
          <button onClick={handleIncrease} className="w-7 h-7 flex items-center justify-center bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-sm">
            <Plus className="w-3 h-3 text-white" />
          </button>
          <button onClick={handleRemove} className="ml-auto text-red-400 hover:text-red-300">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyCart({ onClose }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <ShoppingBag className="w-16 h-16 text-[#3f3f46] mb-4" />
      <p className="text-[#A1A1AA] mb-2">Your cart is empty</p>
      <Button onClick={onClose} className="btn-secondary mt-4">Continue Shopping</Button>
    </div>
  );
}

function CartItemsList({ items, onUpdateQty, onRemove }) {
  return (
    <div className="space-y-4">
      {items.map(function(item) {
        return <CartItem key={item.cartItemId || item.id} item={item} onUpdateQty={onUpdateQty} onRemove={onRemove} />;
      })}
    </div>
  );
}

function CartFooter({ total, onCheckout, onClear }) {
  return (
    <div className="p-6 border-t border-white/10 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-[#A1A1AA]">Subtotal</span>
        <span className="font-heading font-bold text-white text-xl">{formatPrice(total)}</span>
      </div>
      <p className="text-xs text-[#71717a]">Shipping calculated at checkout</p>
      <Button className="btn-primary w-full" onClick={onCheckout} data-testid="checkout-button">
        <CreditCard className="w-4 h-4 mr-2" />Continue to Checkout
      </Button>
      <button onClick={onClear} className="w-full text-center text-sm text-[#A1A1AA] hover:text-white">Clear Cart</button>
    </div>
  );
}

export default function CartDrawer() {
  var navigate = useNavigate();
  var cart = useCart();
  var items = cart.cartItems;
  var itemCount = items.length;
  var isOpen = cart.isCartOpen;

  function handleCheckout() {
    cart.setIsCartOpen(false);
    navigate("/checkout");
  }

  function closeCart() { cart.setIsCartOpen(false); }

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50" onClick={closeCart} data-testid="cart-backdrop" />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#0a0a0a] border-l border-white/10 z-50 flex flex-col" data-testid="cart-drawer">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-[#C0A062]" />
            <h2 className="font-heading font-bold text-white text-xl uppercase">Your Cart</h2>
            <span className="px-2 py-0.5 bg-[#C0A062] text-black text-xs font-mono rounded-sm">{itemCount}</span>
          </div>
          <button onClick={closeCart} className="text-[#A1A1AA] hover:text-white" data-testid="close-cart">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {itemCount === 0 ? (
            <EmptyCart onClose={closeCart} />
          ) : (
            <CartItemsList items={items} onUpdateQty={cart.updateQuantity} onRemove={cart.removeFromCart} />
          )}
        </div>

        {itemCount > 0 && (
          <CartFooter total={cart.getCartTotal()} onCheckout={handleCheckout} onClear={cart.clearCart} />
        )}
      </div>
    </>
  );
}
